// src/pages/admin/OrdersList.js
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { Button } from "@mui/material";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import { Box } from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, ModalClose, Sheet, Typography } from "@mui/joy";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

export default function OrdersList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [callApi, setCallApi] = useState(false);
  const [warning, setWarning] = useState({ text: "", type: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);

  // fetch orders
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}common/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setOrders(r.data))
      .catch(() => {
        alert("Session expired");
        localStorage.clear();
        navigate("/login");
      });
  }, [token, callApi, navigate]);

  // toggle active
  const toggleStatus = async (id) => {
    const ord = orders.find((o) => o._id === id);
    await axios.put(
      `${process.env.REACT_APP_BASE_URL}common/orders/${id}`,
      { active: !ord.active },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCallApi((c) => !c);
  };

  // delete
  const deleteOrder = async (id) => {
    await axios.delete(`${process.env.REACT_APP_BASE_URL}common/orders/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCallApi((c) => !c);
  };

  // prepare table columns
  const columns = [
    { field: "s_no", headerName: "S.no", width: 80 },
    { field: "orderNo", headerName: "Order No", width: 150 },
    { field: "orderTime", headerName: "Order Time", width: 200 },
    { field: "orderBy", headerName: "Order By", width: 150 },
    {
      field: "lines",
      headerName: "Details",
      width: 600,
      sortable: false,
      renderCell: (params) => {
        const order = orders.find(o => o._id === params.row.id);
        return (
          <table className="table table-sm table-bordered mb-0" style={{fontSize:"0.75rem"}}>
            <thead>
              <tr>
                <th>#</th>
                <th>Job</th>
                <th>PN</th>
                <th>Description</th>
                <th>Qty</th>
                <th>UOM</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((ln, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{ln.job.jobNumber}</td>
                  <td>{ln.itemMaster.itemPN}</td>
                  <td>{ln.description}</td>
                  <td>{ln.quantity}</td>
                  <td>{ln.uom}</td>
                  <td>{ln.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const order = orders.find(o => o._id === params.row.id);
        return (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              size="small"
              variant="contained"
              color={order.active ? "success" : "error"}
              onClick={() => {
                setSelectedId(params.row.id);
                setWarning({
                  text: `Change status to ${order.active ? "Inactive" : "Active"}?`,
                  type: "status",
                });
                setOpenWarning(true);
              }}
            >
              {order.active ? "Active" : "Inactive"}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => {
                setSelectedId(params.row.id);
                setWarning({ text: "Delete this order?", type: "delete" });
                setOpenWarning(true);
              }}
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
        );
      }
    },
  ];

  // prepare table rows
  const rows = orders.map((o, idx) => ({
    id: o._id,
    s_no: idx + 1,
    orderNo: o.orderNo,
    orderTime: new Date(o.orderTime).toLocaleString(),
    orderBy: o.orderBy?.username || "Admin",
  }));

  return (
    <PageLayout>
      <Grid container spacing={2} className="mb-2 align-items-center">
        <Grid size={{ xs: 12, md: 6 }}>
          <Breadcrumb title="Orders List" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} className="text-end">
          <Link to="/create-order">
            <Button variant="contained" color="primary">Create Order</Button>
          </Link>
        </Grid>
      </Grid>

      <Box className="mc-card">
        <CardHeader title="Orders" />
        <div style={{ height: 600, width: '100%', marginTop: "1rem" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1,
              },
            }}
          />
        </div>
      </Box>

      {/* Confirmation Modal */}
      <Modal
        open={openWarning}
        onClose={() => setOpenWarning(false)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Sheet
          variant="outlined"
          sx={{
            width: "30%",
            p: 3,
            borderRadius: "md",
            boxShadow: "lg",
            bgcolor: "#fff",
          }}
        >
          <ModalClose sx={{ top: 8, right: 8 }} />
          <Typography fontSize="1.25em" mb={3}>
            {warning.text}
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "2rem",
            }}
          >
            <Button
              variant="contained"
              color="success"
              style={{ width: "8rem" }}
              onClick={() => {
                if (warning.type === "status") toggleStatus(selectedId);
                else deleteOrder(selectedId);
                setOpenWarning(false);
              }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              color="error"
              style={{ width: "8rem" }}
              onClick={() => setOpenWarning(false)}
            >
              No
            </Button>
          </div>
        </Sheet>
      </Modal>
    </PageLayout>
  );
}
